(function () {
  /**
   * @typedef {{
   *   id: string,
   *   caption: string,
   *   title: string,
   *   text: string,
   *   hashtags: string[],
   *   url: string,
   *   thumbnailUrl?: string
   * }} SocialFeedVideo
   */

  /**
   * @callback BuildEndpoint
   * @param {HTMLElement} root
   * @returns {string | null}
   */

  /**
   * @callback GetIcon
   * @param {string} name
   * @returns {string}
   */

  /**
   * @callback BuildPlayerUrl
   * @param {string} videoId
   * @param {'card' | 'modal'} [mode]
   * @returns {string}
   */

  /**
   * @callback SendPlayerMessage
   * @param {HTMLElement} container
   * @param {string} type
   * @returns {void}
   */

  /**
   * @callback SetMuted
   * @param {HTMLElement} reel
   * @param {boolean} muted
   * @returns {void}
   */

  /**
   * @callback LoadPlayer
   * @param {HTMLElement} reel
   * @returns {void}
   */

  /**
   * @callback CreateReel
   * @param {SocialFeedVideo} video
   * @returns {HTMLElement}
   */

  /**
   * @callback NormalizeVideo
   * @param {any} raw
   * @returns {SocialFeedVideo | null}
   */

  /**
   * @callback GetVideoUrl
   * @param {SocialFeedVideo} video
   * @param {string} profileUrl
   * @returns {string}
   */

  /**
   * @callback FetchVideos
   * @param {HTMLElement} root
   * @returns {Promise<SocialFeedVideo[]>}
   */

  /**
   * @callback RenderVideos
   * @param {HTMLElement} carousel
   * @param {SocialFeedVideo[]} videos
   * @returns {void}
   */

  /**
   * @callback OpenModal
   * @param {number} index
   * @returns {void}
   */

  /**
   * @callback InitModal
   * @param {HTMLElement} root
   * @param {HTMLElement} carousel
   * @param {SocialFeedVideo[]} videos
   * @returns {OpenModal | null}
   */

  /**
   * @callback InitPlayers
   * @param {HTMLElement} carousel
   * @param {OpenModal | null} openModal
   * @returns {void}
   */

  /**
   * @callback FindReelForFrame
   * @param {MessageEventSource | null} source
   * @returns {HTMLElement | null}
   */

  /**
   * @callback InitCarousel
   * @param {HTMLElement} root
   * @param {HTMLElement} carousel
   * @returns {() => void}
   */

  /**
   * @callback Init
   * @param {HTMLElement} root
   * @returns {void}
   */

  /**
   * @callback InitAll
   * @param {ParentNode} [scope]
   * @returns {void}
   */

  var SCROLL_AMOUNT = 320;
  var TIKTOK_ORIGIN = "https://www.tiktok.com";
  var HASHTAG_RE = /#[\p{L}\p{N}_]+/gu;

  // Small card players: silent, no controls. Popup player: full TikTok controls.
  var PLAYER_PARAMS = {
    card: [
      "autoplay=1",
      "loop=1",
      "muted=1",
      "controls=0",
      "progress_bar=0",
      "play_button=0",
      "volume_control=0",
      "fullscreen_button=0",
      "timestamp=0",
      "music_info=0",
      "description=0",
      "rel=0",
      "native_context_menu=0",
      "closed_caption=0",
    ],
    modal: [
      "autoplay=1",
      "loop=1",
      "controls=1",
      "progress_bar=1",
      "play_button=1",
      "volume_control=1",
      "fullscreen_button=1",
      "timestamp=1",
      "music_info=1",
      "description=1",
      "rel=0",
      "closed_caption=1",
    ],
  };

  /** @type {BuildEndpoint} */
  var buildEndpoint = function (root) {
    var base = (root.dataset.apiBase || "").replace(/\/+$/, "");
    var storeCode = (root.dataset.storeCode || "").trim();
    var count = parseInt(root.dataset.count || "", 10) || 6;

    if (!base || !storeCode) return null;

    return base + "/" + encodeURIComponent(storeCode) + "?count=" + count;
  };

  /** @type {GetIcon} */
  var getIcon = function (name) {
    var template = document.querySelector(
      'template[data-social-feed-icon="' + name + '"]',
    );
    return template ? template.innerHTML : "";
  };

  /** @type {BuildPlayerUrl} */
  var buildPlayerUrl = function (videoId, mode) {
    return (
      TIKTOK_ORIGIN +
      "/player/v1/" +
      encodeURIComponent(videoId) +
      "?" +
      PLAYER_PARAMS[mode || "card"].join("&")
    );
  };

  /** @type {SendPlayerMessage} */
  var sendPlayerMessage = function (container, type) {
    var iframe = container.querySelector("iframe");
    if (!iframe || !iframe.contentWindow) return;

    iframe.contentWindow.postMessage(
      { "x-tiktok-player": true, type: type },
      TIKTOK_ORIGIN,
    );
  };

  /** @type {SetMuted} */
  var setMuted = function (reel, muted) {
    var button = reel.querySelector("[data-social-feed-mute]");

    reel.dataset.muted = muted ? "true" : "false";
    sendPlayerMessage(reel, muted ? "mute" : "unMute");

    if (button) {
      button.innerHTML = getIcon(muted ? "muted" : "unmuted");
      button.setAttribute("aria-label", muted ? "Unmute video" : "Mute video");
      button.setAttribute("aria-pressed", muted ? "false" : "true");
    }
  };

  /** @type {LoadPlayer} */
  var loadPlayer = function (reel) {
    if (reel.querySelector("iframe")) return;

    var iframe = document.createElement("iframe");
    iframe.className = "social-feed__player";
    iframe.src = buildPlayerUrl(reel.dataset.videoId || "", "card");
    iframe.title = reel.dataset.caption || "TikTok video";
    iframe.allow = "autoplay; encrypted-media; fullscreen; picture-in-picture";
    iframe.setAttribute("tabindex", "-1");
    // Safari draws the player page's own scrollbars inside the card without this.
    iframe.setAttribute("scrolling", "no");
    iframe.addEventListener("load", function () {
      setTimeout(function () {
        reel.classList.add("is-playing");
      }, 800);
    });
    reel.insertBefore(iframe, reel.firstChild);
  };

  /** @type {CreateReel} */
  var createReel = function (video) {
    var reel = document.createElement("div");
    reel.className = "social-feed__reel social-feed__reel--tiktok";
    reel.dataset.videoId = video.id;
    reel.dataset.caption = video.caption || "";
    reel.dataset.muted = "true";

    // The whole card opens the popup, so make it keyboard-accessible.
    reel.tabIndex = 0;
    reel.setAttribute("role", "button");
    reel.setAttribute(
      "aria-label",
      "Open video" +
        (video.title || video.text ? ": " + (video.title || video.text) : ""),
    );

    if (video.thumbnailUrl) {
      var img = document.createElement("img");
      img.className = "social-feed__poster";
      img.src = video.thumbnailUrl;
      img.alt = "";
      img.loading = "lazy";
      img.width = 300;
      img.height = 400;
      reel.appendChild(img);
    }

    var mute = document.createElement("button");
    mute.type = "button";
    mute.className = "social-feed__mute";
    mute.setAttribute("data-social-feed-mute", "");
    reel.appendChild(mute);

    setMuted(reel, true);

    return reel;
  };

  /** @type {NormalizeVideo} */
  var normalizeVideo = function (raw) {
    if (!raw || !raw.id) return null;

    var caption = String(raw.caption || raw.description || "");

    // Hashtags: use the API list if there is one, plus any #tags found in the caption.
    /** @type {string[]} */
    var hashtags = [];
    /** @type {Record<string, boolean>} */
    var seen = {};
    var apiTags = Array.isArray(raw.hashtags) ? raw.hashtags : [];
    apiTags
      .concat(caption.match(HASHTAG_RE) || [])
      .forEach(function (/** @type {any} */ tag) {
        var clean = String(tag).replace(/^#/, "").trim();
        var key = clean.toLowerCase();
        if (clean && !seen[key]) {
          seen[key] = true;
          hashtags.push(clean);
        }
      });

    // Caption text without the hashtags, for the description paragraph.
    var text = caption.replace(HASHTAG_RE, "").replace(/\s+/g, " ").trim();

    return {
      id: String(raw.id),
      caption: caption,
      title: String(raw.title || "").trim(),
      text: text,
      hashtags: hashtags,
      url: String(raw.url || raw.shareUrl || raw.permalink || ""),
      thumbnailUrl: raw.thumbnailUrl,
    };
  };

  /** @type {GetVideoUrl} */
  var getVideoUrl = function (video, profileUrl) {
    if (video.url) return video.url;

    // Build https://www.tiktok.com/@handle/video/<id> from the TikTok URL setting.
    var profile = ((profileUrl || "").split(/[?#]/)[0] || "").replace(
      /\/+$/,
      "",
    );
    if (/tiktok\.com\/@[^/]+$/i.test(profile)) {
      return profile + "/video/" + encodeURIComponent(video.id);
    }

    return TIKTOK_ORIGIN + "/player/v1/" + encodeURIComponent(video.id);
  };

  /** @type {FetchVideos} */
  var fetchVideos = function (root) {
    var endpoint = buildEndpoint(root);
    if (!endpoint) return Promise.resolve([]);

    return fetch(endpoint, { headers: { Accept: "application/json" } })
      .then(function (response) {
        if (!response.ok)
          throw new Error("Social feed request failed: " + response.status);
        return response.json();
      })
      .then(function (data) {
        var videos = data && data.result && data.result.videos;
        if (!Array.isArray(videos)) return [];
        return /** @type {SocialFeedVideo[]} */ (
          videos.map(normalizeVideo).filter(Boolean)
        );
      })
      .catch(function (error) {
        console.warn(error);
        return [];
      });
  };

  /** @type {RenderVideos} */
  var renderVideos = function (carousel, videos) {
    var fragment = document.createDocumentFragment();
    videos.forEach(function (video) {
      fragment.appendChild(createReel(video));
    });

    carousel.replaceChildren(fragment);
    carousel.scrollLeft = 0;
  };

  /** @type {InitModal} */
  var initModal = function (root, carousel, videos) {
    var modal = /** @type {HTMLDialogElement | null} */ (
      root.querySelector("[data-social-feed-modal]")
    );
    if (!modal || typeof modal.showModal !== "function" || !videos.length)
      return null;

    var dialog = modal;

    /** @param {string} selector */
    var find = function (selector) {
      return /** @type {HTMLElement | null} */ (dialog.querySelector(selector));
    };

    var playerWrap = /** @type {HTMLElement} */ (
      find("[data-social-feed-modal-player]")
    );
    var titleEl = find("[data-social-feed-modal-title]");
    var titleText = find("[data-social-feed-modal-title-text]");
    var captionEl = find("[data-social-feed-modal-caption]");
    var tagsEl = find("[data-social-feed-modal-tags]");
    var ctaEl = /** @type {HTMLAnchorElement | null} */ (
      find("[data-social-feed-modal-cta]")
    );
    var counterEl = find("[data-social-feed-modal-counter]");
    var navEl = find("[data-social-feed-modal-nav]");
    var profileUrl = root.dataset.tiktokProfile || "";
    var current = 0;

    // Hide prev/next when there is only one video.
    if (navEl) navEl.hidden = videos.length < 2;

    /** @param {(reel: HTMLElement) => void} fn */
    var eachReel = function (fn) {
      carousel
        .querySelectorAll(".social-feed__reel--tiktok")
        .forEach(function (reel) {
          fn(/** @type {HTMLElement} */ (reel));
        });
    };

    /** @param {number} index */
    var render = function (index) {
      current = (index + videos.length) % videos.length;
      var video = videos[current];
      if (!video) return;

      // Player
      var iframe = document.createElement("iframe");
      iframe.className = "social-feed__modal-iframe";
      iframe.src = buildPlayerUrl(video.id, "modal");
      iframe.title = video.title || video.text || "TikTok video";
      iframe.allow =
        "autoplay; encrypted-media; fullscreen; picture-in-picture; clipboard-write; web-share";
      iframe.allowFullscreen = true;
      iframe.setAttribute("scrolling", "no");
      playerWrap.replaceChildren(iframe);

      // Title
      if (titleEl && titleText) {
        titleText.textContent = video.title;
        titleEl.hidden = !video.title;
      }

      // Description
      if (captionEl) {
        captionEl.textContent = video.text;
        captionEl.hidden = !video.text;
      }

      // Hashtag pills
      if (tagsEl) {
        tagsEl.replaceChildren();
        video.hashtags.forEach(function (tag) {
          var pill = document.createElement("a");
          pill.className = "social-feed__modal-tag";
          pill.href = TIKTOK_ORIGIN + "/tag/" + encodeURIComponent(tag);
          pill.target = "_blank";
          pill.rel = "noopener noreferrer";
          pill.textContent = "#" + tag;
          /** @type {HTMLElement} */ (tagsEl).appendChild(pill);
        });
        tagsEl.hidden = !video.hashtags.length;
      }

      // Watch on TikTok
      if (ctaEl) ctaEl.href = getVideoUrl(video, profileUrl);

      // "2 / 6" counter (mobile)
      if (counterEl)
        counterEl.textContent = current + 1 + " / " + videos.length;

      dialog.scrollTop = 0;
    };

    /** @type {OpenModal} */
    var open = function (index) {
      eachReel(function (reel) {
        sendPlayerMessage(reel, "pause");
      });
      render(index);
      document.documentElement.style.overflow = "hidden";
      dialog.showModal();
    };

    dialog.addEventListener("close", function () {
      // Removing the iframe stops the video and its audio.
      playerWrap.replaceChildren();
      document.documentElement.style.overflow = "";
      eachReel(function (reel) {
        if (reel.dataset.visible !== "false") sendPlayerMessage(reel, "play");
      });
    });

    dialog.addEventListener("click", function (event) {
      var target = /** @type {HTMLElement} */ (event.target);

      if (target.closest("[data-social-feed-modal-prev]")) {
        render(current - 1);
        return;
      }
      if (target.closest("[data-social-feed-modal-next]")) {
        render(current + 1);
        return;
      }
      if (
        target.closest("[data-social-feed-modal-close]") ||
        target === dialog ||
        target.hasAttribute("data-social-feed-modal-backdrop")
      ) {
        dialog.close();
      }
    });

    dialog.addEventListener("keydown", function (event) {
      if (videos.length < 2) return;
      if (event.key === "ArrowLeft") render(current - 1);
      if (event.key === "ArrowRight") render(current + 1);
    });

    return open;
  };

  /** @type {InitPlayers} */
  var initPlayers = function (carousel, openModal) {
    /** @type {HTMLElement[]} */
    var reels = Array.prototype.slice.call(
      carousel.querySelectorAll(".social-feed__reel--tiktok"),
    );
    if (!reels.length) return;

    // Clicking the mute button toggles sound (one video at a time).
    // Clicking anywhere else on the card opens the popup.
    carousel.addEventListener("click", function (event) {
      var target = /** @type {Element} */ (event.target);
      var reel = /** @type {HTMLElement | null} */ (
        target.closest(".social-feed__reel--tiktok")
      );
      if (!reel) return;

      var button = target.closest("[data-social-feed-mute]");
      if (!button) {
        if (openModal) openModal(reels.indexOf(reel));
        return;
      }

      var unmute = reel.dataset.muted === "true";

      if (unmute) {
        reels.forEach(function (other) {
          if (other !== reel && other.dataset.muted === "false")
            setMuted(other, true);
        });
      }

      setMuted(reel, !unmute);
    });

    carousel.addEventListener("keydown", function (event) {
      var target = /** @type {HTMLElement} */ (event.target);
      if (
        (event.key === "Enter" || event.key === " ") &&
        target.classList.contains("social-feed__reel--tiktok") &&
        openModal
      ) {
        event.preventDefault();
        openModal(reels.indexOf(target));
      }
    });

    if (!("IntersectionObserver" in window)) {
      reels.forEach(loadPlayer);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var reel = /** @type {HTMLElement} */ (entry.target);
          reel.dataset.visible = entry.isIntersecting ? "true" : "false";

          if (entry.isIntersecting) {
            loadPlayer(reel);
            sendPlayerMessage(reel, "play");
          } else {
            sendPlayerMessage(reel, "pause");
          }
        });
      },
      { threshold: 0.25 },
    );

    reels.forEach(function (reel) {
      observer.observe(reel);
    });
  };

  /** @type {FindReelForFrame} */
  var findReelForFrame = function (source) {
    /** @type {NodeListOf<HTMLIFrameElement>} */
    var iframes = document.querySelectorAll(".social-feed__player");
    for (var i = 0; i < iframes.length; i++) {
      var iframe = iframes[i];
      if (iframe && iframe.contentWindow === source)
        return iframe.closest(".social-feed__reel--tiktok");
    }
    return null;
  };

  window.addEventListener("message", function (event) {
    if (event.origin !== TIKTOK_ORIGIN) return;

    var data = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return;
      }
    }
    if (!data || !data["x-tiktok-player"]) return;

    // Popup player: try to play with sound, fall back to muted if blocked.
    var modalFrame = /** @type {HTMLIFrameElement | null} */ (
      document.querySelector(".social-feed__modal-iframe")
    );
    if (modalFrame && modalFrame.contentWindow === event.source) {
      var wrap = /** @type {HTMLElement} */ (modalFrame.parentElement);

      if (data.type === "onPlayerReady") {
        sendPlayerMessage(wrap, "unMute");
        sendPlayerMessage(wrap, "play");
      }

      if (
        data.type === "onPlayerError" &&
        data.value &&
        data.value.errorCode === 3002
      ) {
        sendPlayerMessage(wrap, "mute");
        sendPlayerMessage(wrap, "play");
      }
      return;
    }

    // Card players.
    var reel = findReelForFrame(event.source);
    if (!reel) return;

    if (data.type === "onPlayerReady") {
      sendPlayerMessage(
        reel,
        reel.dataset.muted === "true" ? "mute" : "unMute",
      );
      sendPlayerMessage(
        reel,
        reel.dataset.visible === "false" ? "pause" : "play",
      );
    }

    if (data.type === "onStateChange" && data.value === 1) {
      reel.classList.add("is-playing");
    }
  });

  /** @type {InitCarousel} */
  var initCarousel = function (root, carousel) {
    /** @type {HTMLButtonElement[]} */
    var prevButtons = Array.prototype.slice.call(
      root.querySelectorAll("[data-social-feed-prev]"),
    );
    /** @type {HTMLButtonElement[]} */
    var nextButtons = Array.prototype.slice.call(
      root.querySelectorAll("[data-social-feed-next]"),
    );

    function updateArrowState() {
      var maxScroll = carousel.scrollWidth - carousel.clientWidth;
      prevButtons.forEach(function (btn) {
        btn.disabled = carousel.scrollLeft <= 0;
      });
      nextButtons.forEach(function (btn) {
        btn.disabled = carousel.scrollLeft >= maxScroll - 1;
      });
    }

    nextButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        carousel.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
      });
    });

    prevButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        carousel.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
      });
    });

    carousel.addEventListener("scroll", updateArrowState);
    window.addEventListener("resize", updateArrowState);
    updateArrowState();

    return updateArrowState;
  };

  /** @type {Init} */
  var init = function (root) {
    if (root.dataset.socialFeedInit) return;
    root.dataset.socialFeedInit = "true";

    /** @type {HTMLElement | null} */
    var carousel = root.querySelector("[data-social-feed-carousel]");
    if (!carousel) return;

    var updateArrowState = initCarousel(root, carousel);

    root.classList.add("is-loading");
    fetchVideos(root).then(function (videos) {
      root
        .querySelectorAll("[data-social-feed-skeleton]")
        .forEach(function (skeleton) {
          skeleton.remove();
        });
      root.classList.remove("is-loading");
      if (!videos.length || !carousel) return;

      renderVideos(carousel, videos);
      initPlayers(carousel, initModal(root, carousel, videos));
      updateArrowState();
    });
  };

  /** @type {InitAll} */
  var initAll = function (scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll("[data-social-feed-root]"),
      init,
    );
  };

  initAll();

  document.addEventListener("shopify:section:load", function (event) {
    initAll(/** @type {Element} */ (event.target));
  });
})();
